(() => {
  if (customElements.get('yumi-product-expander')) return;

  class YumiProductExpander extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      if (!this.initialized) {
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        const initial = Number(mobile ? this.dataset.mobileCount : this.dataset.initialCount);
        this.items().forEach((item, index) => { item.hidden = index >= initial; });
        this.initialized = true;
      }
      this.dataset.ready = 'true';
      this.bindControl(this.querySelector('.yd-collection-button a'));
      this.addEventListener('click', event => {
        const control = event.target.closest('[data-expand-control]');
        if (!control || control.dataset.expandControl !== this.id) return;
        event.preventDefault();
        this.expand(control);
      }, options);
      this.addEventListener('keydown', event => {
        const control = event.target.closest('[data-expand-control]');
        if (!control || control.dataset.expandControl !== this.id || event.key !== ' ') return;
        event.preventDefault();
        this.expand(control);
      }, options);
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.request?.abort();
    }

    reset() {
      this.request?.abort();
      this.initialized = false;
      this.expanded = false;
      this.connectedCallback();
    }

    grid() {
      return this.querySelector(this.dataset.gridSelector);
    }

    items() {
      return [...(this.grid()?.children || [])];
    }

    bindControl(control) {
      if (!control || !this.grid()) return;
      this.control = control;
      control.dataset.expandControl = this.id;
      control.setAttribute('role', 'button');
      control.setAttribute('aria-controls', this.id);
      control.setAttribute('aria-expanded', String(Boolean(this.expanded)));
      control.hidden = !this.hasMore();
    }

    hasMore() {
      return this.items().some(item => item.hidden) || Boolean(this.querySelector('[data-expand-page]')?.dataset.nextUrl);
    }

    async loadPage() {
      const page = this.querySelector('[data-expand-page]');
      const next = page?.dataset.nextUrl;
      if (!next) return;
      const url = new URL(next, location.origin);
      if (url.origin !== location.origin) throw new Error('Unexpected pagination origin');
      url.searchParams.set('section_id', this.dataset.sectionId);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const signal = this.request.signal;
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error('Product page could not be loaded');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      signal.throwIfAborted();
      const incoming = document.getElementById(this.id);
      const incomingGrid = incoming?.querySelector(this.dataset.gridSelector);
      if (!incomingGrid || incoming.dataset.collectionId !== this.dataset.collectionId) {
        throw new Error('Product page did not match the requested collection');
      }
      if (!this.isConnected) return;
      const stylesheet = document.querySelector('style[data-section-stylesheet]');
      if (stylesheet) this.closest('.shopify-section')?.append(stylesheet);
      const existing = new Set(this.items().map(item => item.querySelector('a[href*="/products/"]')?.getAttribute('href')));
      for (const item of [...incomingGrid.children]) {
        const href = item.querySelector('a[href*="/products/"]')?.getAttribute('href');
        if (!href || existing.has(href)) continue;
        existing.add(href);
        const suffix = `-expand-${this.items().length}`;
        const ids = new Map();
        for (const element of [item, ...item.querySelectorAll('[id]')]) {
          if (!element.id) continue;
          ids.set(element.id, element.id + suffix);
          element.id += suffix;
        }
        for (const element of [item, ...item.querySelectorAll('*')]) {
          for (const attribute of ['for', 'aria-labelledby', 'aria-describedby', 'aria-controls']) {
            const value = element.getAttribute(attribute);
            if (value) element.setAttribute(attribute, value.split(' ').map(id => ids.get(id) || id).join(' '));
          }
          const target = element.getAttribute('href');
          if (target?.startsWith('#') && ids.has(target.slice(1))) element.setAttribute('href', '#' + ids.get(target.slice(1)));
        }
        item.hidden = true;
        this.grid().append(item);
      }
      const following = incoming.querySelector('[data-expand-page]')?.dataset.nextUrl || '';
      if (following === next) throw new Error('Product pagination did not advance');
      page.dataset.nextUrl = following;
    }

    async expand(control) {
      if (this.loading) return;
      this.loading = true;
      this.request = new AbortController();
      control.setAttribute('aria-busy', 'true');
      control.setAttribute('aria-disabled', 'true');
      const status = this.querySelector('[data-expand-status]');
      if (status) { status.textContent = ''; status.hidden = true; }
      try {
        while (this.items().filter(item => item.hidden).length < 6 && this.querySelector('[data-expand-page]')?.dataset.nextUrl) {
          await this.loadPage();
        }
        if (!this.isConnected) return;
        const added = this.items().filter(item => item.hidden).slice(0, 6);
        for (const item of added) item.hidden = false;
        this.expanded = true;
        control.setAttribute('aria-expanded', 'true');
        control.hidden = !this.hasMore();
        added[0]?.querySelector('a[href*="/products/"]')?.focus({ preventScroll: true });
      } catch (error) {
        if (error?.name !== 'AbortError' && this.isConnected && status) {
          status.textContent = this.dataset.errorLabel;
          status.hidden = false;
        }
      } finally {
        this.loading = false;
        control.removeAttribute('aria-busy');
        control.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('yumi-product-expander', YumiProductExpander);
})();
